<template>
  <menu-content-container
    :header="label || $t('forms.margin')"
    border-full
    :padding-left-right="paddingLeftRight"
  >
    <IndependentValue
      :header="label || $t('forms.margin')"
      :default-open="isMixed"
    >
      <template #independent-to-expand>
        <content-row class="!justify-end">
          <t-input-number
            v-model="paddingt"
            data-testid="top-padding"
            :regex-pattern="'\d+'"
            :min="minY"
            :max="maxY"
            :label="$t('forms.top')"
            unit=""
            flex
            :changed="changedTop"
            @reset="resetDirection('top')"
          >
            <template #label>
              <Side class="rotate-90" />
            </template>
          </t-input-number>
          <t-input-number
            v-model="paddingl"
            data-testid="left-padding"
            :regex-pattern="'\d+'"
            :min="minX"
            :max="maxX"
            :label="$t('forms.left')"
            unit=""
            flex
            :changed="changedLeft"
            @reset="resetDirection('left')"
          >
            <template #label>
              <Side />
            </template>
          </t-input-number>
        </content-row>
        <content-row class="!justify-end">
          <t-input-number
            v-model="paddingb"
            data-testid="bottom-padding"
            :regex-pattern="'\d+'"
            :min="minY"
            :max="maxY"
            :label="$t('forms.bottom')"
            unit=""
            flex
            :changed="changedBottom"
            @reset="resetDirection('bottom')"
          >
            <template #label>
              <Side class="rotate-[270deg]" />
            </template>
          </t-input-number>
          <t-input-number
            v-model="paddingr"
            data-testid="right-padding"
            :regex-pattern="'\d+'"
            :min="minX"
            :max="maxX"
            :label="$t('forms.right')"
            unit=""
            flex
            :changed="changedRight"
            @reset="resetDirection('right')"
          >
            <template #label>
              <Side class="rotate-180" />
            </template>
          </t-input-number>
        </content-row>
      </template>
      <TInputCombinated
        v-model="commonPadding"
        :label="inputLabel || `${$t('forms.margin')}`"
        :min="min"
        :max="max"
        :disabled="disabled"
        :units="['px']"
        :width="40"
        flex
        class="w-full"
        data-testid="common-padding"
        :changed="changed"
        @reset="emits('reset')"
      />
    </IndependentValue>
  </menu-content-container>
</template>

<script lang="ts" setup>
  import IndependentValue from "./components/IndependentValue/IndependentValue.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { resolveDisplayedSpacing } from "@/utils/spacingUtils";
  import { TInputNumber, TInputCombinated } from "@topol/twig-ui";
  import { Side } from "@topol/icons";
  import { ref, computed, onMounted } from "vue";

  const props = defineProps({
    product: {
      type: Boolean,
      default: false,
    },
    prodstructure: {
      type: [Object, Boolean],
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    inputLabel: {
      type: String,
      default: "",
    },
    toggleLabel: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
      default: "",
    },
    externalUsage: {
      type: Boolean,
      default: false,
    },
    externalPadding: {
      type: String,
      default: undefined,
    },
    minY: {
      type: Number,
      default: 0,
    },
    maxY: {
      type: Number,
      default: 99,
    },
    minX: {
      type: Number,
      default: 0,
    },
    maxX: {
      type: Number,
      default: 99,
    },
    changed: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: String,
      default: undefined,
    },
    paddingLeftRight: {
      type: Number,
      default: undefined,
    },
  });

  const emits = defineEmits(["paddingChanged", "reset"]);

  const detailmargin = ref(false);

  // --- Per-direction default parsing ---
  function parseSpacingParts(val: string | undefined) {
    if (!val) return { top: 0, right: 0, bottom: 0, left: 0 };
    const parts = val.split(" ").map((p) => parseInt(p) || 0);
    if (parts.length === 1)
      return {
        top: parts[0] || 0,
        right: parts[0] || 0,
        bottom: parts[0] || 0,
        left: parts[0] || 0,
      };
    if (parts.length === 2)
      return {
        top: parts[0] || 0,
        right: parts[1] || 0,
        bottom: parts[0] || 0,
        left: parts[1] || 0,
      };
    if (parts.length === 3)
      return {
        top: parts[0] || 0,
        right: parts[1] || 0,
        bottom: parts[2] || 0,
        left: parts[1] || 0,
      };
    return {
      top: parts[0] || 0,
      right: parts[1] || 0,
      bottom: parts[2] || 0,
      left: parts[3] || 0,
    };
  }

  const resolvedDefault = computed(() => props.defaultValue);
  const defaultParts = computed(() => parseSpacingParts(resolvedDefault.value));

  // The padding actually on display, expanded once per change. Reusing
  // parseSpacingParts keeps the displayed sides and defaultParts in agreement
  // (they are compared to decide whether a side shows a reset) and avoids
  // re-reading the store and re-splitting the value for every side.
  const paddingParts = computed(() => parseSpacingParts(getPaddingAttribute()));

  // Each side is "changed" when it differs from its own default. When no
  // defaultValue is provided, defaultParts falls back to 0 for every side, so
  // the reset only shows next to the side that actually differs.
  const changedTop = computed(() => paddingt.value !== defaultParts.value.top);
  const changedRight = computed(
    () => paddingr.value !== defaultParts.value.right
  );
  const changedBottom = computed(
    () => paddingb.value !== defaultParts.value.bottom
  );
  const changedLeft = computed(
    () => paddingl.value !== defaultParts.value.left
  );

  function resetDirection(direction: "top" | "right" | "bottom" | "left") {
    const def = defaultParts.value;
    const t = direction === "top" ? def.top : paddingt.value;
    const r = direction === "right" ? def.right : paddingr.value;
    const b = direction === "bottom" ? def.bottom : paddingb.value;
    const l = direction === "left" ? def.left : paddingl.value;
    setPadding(t + "px " + r + "px " + b + "px " + l + "px");
  }

  const mjmlStore = useMjmlStore();

  const min = computed(() => {
    if (props.minY < props.minX) {
      return props.minX;
    } else {
      return props.minY;
    }
  });

  const max = computed(() => {
    if (props.maxY < props.maxX) {
      return props.maxX;
    } else {
      return props.maxY;
    }
  });

  // Common field: shows a single value when all sides match, "Mixed" otherwise.
  // Returns a "px" string (not a bare number) so that 0 renders as "0" instead
  // of an empty input in TInputCombinated.
  const commonPadding = computed<string>({
    get() {
      const t = paddingt.value || 0;
      const r = paddingr.value || 0;
      const b = paddingb.value || 0;
      const l = paddingl.value || 0;
      if (t === r && r === b && b === l) {
        return `${t}px`;
      }
      return "Mixed";
    },
    set(val) {
      // Setting the common field applies one value to every side. Skip only
      // when all sides already equal it. Comparing against a single side (top)
      // would wrongly treat a "Mixed" state whose top matches as a no-op — e.g.
      // top = 0 made the arrows dead while the other sides still differed.
      const num = parseInt(String(val)) || 0;
      const t = paddingt.value || 0;
      const r = paddingr.value || 0;
      const b = paddingb.value || 0;
      const l = paddingl.value || 0;
      if (t === num && r === num && b === num && l === num) {
        return;
      }
      setPadding(`${num}px`);
    },
  });

  // Auto-expand the per-side detail when the sides differ ("Mixed").
  const isMixed = computed(() => commonPadding.value === "Mixed");

  const paddingt = computed({
    get() {
      return paddingParts.value.top;
    },
    set(val: number) {
      if (val !== paddingt.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          val +
          "px " +
          paddingr.value +
          "px " +
          paddingb.value +
          "px " +
          paddingl.value +
          "px";
        setPadding(setval);
      }
    },
  });

  const paddingr = computed({
    get() {
      return paddingParts.value.right;
    },
    set(val: number) {
      if (val !== paddingr.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          paddingt.value +
          "px " +
          val +
          "px " +
          paddingb.value +
          "px " +
          paddingl.value +
          "px";
        setPadding(setval);
      }
    },
  });

  const paddingb = computed({
    get() {
      return paddingParts.value.bottom;
    },
    set(val: number) {
      if (val !== paddingb.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          paddingt.value +
          "px " +
          paddingr.value +
          "px " +
          val +
          "px " +
          paddingl.value +
          "px";
        setPadding(setval);
      }
    },
  });

  const paddingl = computed({
    get() {
      return paddingParts.value.left;
    },
    set(val: number) {
      if (val !== paddingl.value) {
        if (val === undefined) {
          val = 0;
        }

        const setval =
          paddingt.value +
          "px " +
          paddingr.value +
          "px " +
          paddingb.value +
          "px " +
          val +
          "px";
        setPadding(setval);
      }
    },
  });

  /**
   * Returns the padding to display: the stored override when there is one,
   * otherwise the effective default. Reads externalPadding when externalUsage
   * is enabled, the active block attribute otherwise.
   */
  function getPaddingAttribute() {
    const stored = props.externalUsage
      ? props.externalPadding
      : mjmlStore.getActiveBlockAttrString(props.prefix + "padding");

    return resolveDisplayedSpacing(stored, props.defaultValue);
  }

  /**
   * Emmits event if using exetrnal usage.
   * Updates block attribute if external usage is not enabled.
   */
  function setPadding(pad: string | undefined) {
    if (props.externalUsage) {
      emits("paddingChanged", pad);
    } else {
      mjmlStore.updateBlockAttribute({
        prop: props.prefix + "padding",
        value: pad,
        product: props.product,
        prodstructure: props.prodstructure,
      });
    }
  }

  onMounted(() => {
    detailmargin.value = isMixed.value;

    if (
      !props.externalUsage &&
      !isMixed.value &&
      getPaddingAttribute() === "0px"
    ) {
      setPadding("0px");
    }
  });
</script>
